import React, { useState } from "react";
import { Form, Button, InputGroup, Spinner, Alert } from "react-bootstrap";
import { fetchReviewDataFromExtension } from "../../helpers/extensionScrape";
import { fetchAmazonReviewData } from "../../http/bookAPI";

/**
 * ReviewOwnershipCheck
 * Проверяет, что ссылка на отзыв реально принадлежит этой книге/продукту.
 *
 * props:
 * - allowedAsins: string[]  // ASIN'ы текущей книги (kindle/pb/hc/audio)
 * - initialLink?: string
 * - onVerified: ({ link, asin, data }) => void  // вызывается, если всё ок
 * - onFail?: (reason: string) => void
 */
const ReviewOwnershipCheck = ({ allowedAsins = [], initialLink = "", onVerified, onFail }) => {
    const [url, setUrl] = useState(initialLink);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [okMsg, setOkMsg] = useState("");

    // быстрый парсер ASIN прямо из URL, если расширения/бэка нет
    const tryParseAsinFromUrl = (u) => {
        try {
            const x = new URL(u);
            const qAsin = x.searchParams.get("ASIN");
            if (qAsin && /^[A-Z0-9]{10}$/i.test(qAsin)) return qAsin.toUpperCase();
            const m = x.pathname.match(/\/dp\/([A-Z0-9]{10})/i);
            if (m) return m[1].toUpperCase();
        } catch (_) {}
        return null;
    };

    const handleCheck = async () => {
        const link = (url || "").trim();
        if (!link) {
            setErr("Please paste a valid Amazon review link.");
            setOkMsg("");
            return;
        }

        setLoading(true);
        setErr("");
        setOkMsg("");

        const acceptIfMatched = (data) => {
            const asin = (data?.asin || "").toUpperCase();
            const allowed = allowedAsins.map((s) => String(s || "").toUpperCase());

            if (asin && allowed.includes(asin)) {
                setOkMsg(`Success! This review belongs to this book (ASIN: ${asin}).`);
                setErr("");
                onVerified?.({ link, asin, data });
            } else {
                const reason = asin
                    ? `This review belongs to another product (ASIN: ${asin}).`
                    : "Could not detect ASIN on the review page.";
                setErr(reason);
                setOkMsg("");
                onFail?.(reason);
            }
        };

        try {
            // 1) пробуем через расширение Virtalibry (Chrome)
            const data = await fetchReviewDataFromExtension(link, { timeoutMs: 15000 });
            acceptIfMatched(data);
        } catch {
            // 2) fallback — наш backend-парсер
            try {
                const data = await fetchAmazonReviewData(link);
                if (!data?.asin) {
                    const guess = tryParseAsinFromUrl(link);
                    acceptIfMatched({ ...(data || {}), asin: guess });
                } else {
                    acceptIfMatched(data);
                }
            } catch {
                // 3) вообще ничего не получилось
                const guess = tryParseAsinFromUrl(link);
                if (guess) {
                    acceptIfMatched({ asin: guess });
                } else {
                    const reason =
                        "We received your link, but couldn’t verify it automatically. We’ll review it manually — please don’t send it again.";
                    setErr(reason);
                    setOkMsg("");
                    onFail?.(reason);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* Пояснение как в парсере отзывов */}
            <p className="small text-muted mb-2">
                <h6>To attach a review link to this assignment, it must belong to <b>this exact book / product.</b></h6>
                1) Paste your Amazon review link below, then click <b>“Check & Autofill”</b>. <br />2) We’ll verify it using the
                Virtalibry App (Chrome only) and you can create this Assignment.
                <br />
                If you don’t have the extension, install it here:{" "}
                <a href="https://chromewebstore.google.com/detail/virtalibry-review-capture/kbibcafojenjockmlojmclnjoeakkeag" target="_blank" rel="noreferrer">
                    Virtalibry Chrome Extension
                </a>
                . It works only in Google Chrome.
            </p>

            <Form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleCheck();
                }}
                className="small"
            >
                <InputGroup size="sm" className="mb-0 border-success">
                    <Form.Control
                        size="sm"
                        type="text"
                        placeholder="Paste your Amazon review link…"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        disabled={loading}
                        className="border-success"
                    />
                    <Button
                        size="sm"
                        variant="success"
                        onClick={handleCheck}
                        disabled={loading}
                        type="submit"
                        className="px-2"
                    >
                        {loading ? <Spinner animation="border" size="sm" /> : "Check & Autofill"}
                    </Button>
                </InputGroup>
            </Form>

            {/* Ошибка */}
            {err && (
                <Alert variant="danger" className="mt-2 mb-0 py-2 px-3 small">
                    {err}
                </Alert>
            )}

            {/* Успех */}
            {okMsg && (
                <Alert variant="success" className="mt-2 mb-0 py-2 px-3 small">
                    {okMsg}
                </Alert>
            )}
        </>
    );
};

export default ReviewOwnershipCheck;
